/**
 * Plan Limits Enforcement Utility
 * Enforces subscription plan limits and feature access
 */

import { PlanType, Subscription, SubscriptionStatus } from '../services/SubscriptionService';

export interface PlanLimits {
  maxMembers: number;
  maxCustomChallenges: number;
  hasAdminPortal: boolean;
  hasAnalytics: boolean;
  hasRewards: boolean;
  hasWhiteLabel: boolean;
  hasCustomIntegrations: boolean;
  hasPrioritySupport: boolean;
  canEditChallengeDates: boolean;
  canReopenChallenges: boolean;
  canCreateCustomChallenges: boolean;
  canAddFamilyMembers: boolean;
  maxFamilyMembers: number;
}

/**
 * Get plan limits based on plan type
 */
export const getPlanLimits = (planType: PlanType): PlanLimits => {
  switch (planType) {
    case 'individual':
      return {
        maxMembers: 4, // 1 user + 3 family members
        maxCustomChallenges: 0,
        hasAdminPortal: false,
        hasAnalytics: false,
        hasRewards: false,
        hasWhiteLabel: false,
        hasCustomIntegrations: false,
        hasPrioritySupport: false,
        canEditChallengeDates: false,
        canReopenChallenges: false,
        canCreateCustomChallenges: false,
        canAddFamilyMembers: true,
        maxFamilyMembers: 3,
      };
    case 'starter':
      return {
        maxMembers: 100,
        maxCustomChallenges: 0,
        hasAdminPortal: true,
        hasAnalytics: false,
        hasRewards: false,
        hasWhiteLabel: false,
        hasCustomIntegrations: false,
        hasPrioritySupport: false,
        canEditChallengeDates: true,
        canReopenChallenges: true,
        canCreateCustomChallenges: false,
        canAddFamilyMembers: false,
        maxFamilyMembers: 0,
      };
    case 'professional':
      return {
        maxMembers: 500,
        maxCustomChallenges: 10,
        hasAdminPortal: true,
        hasAnalytics: true,
        hasRewards: true,
        hasWhiteLabel: false,
        hasCustomIntegrations: false,
        hasPrioritySupport: true,
        canEditChallengeDates: true,
        canReopenChallenges: true,
        canCreateCustomChallenges: true,
        canAddFamilyMembers: false,
        maxFamilyMembers: 0,
      };
    case 'enterprise':
      return {
        maxMembers: 100000, // Effectively unlimited
        maxCustomChallenges: -1, // Unlimited
        hasAdminPortal: true,
        hasAnalytics: true,
        hasRewards: true,
        hasWhiteLabel: true,
        hasCustomIntegrations: true,
        hasPrioritySupport: true,
        canEditChallengeDates: true,
        canReopenChallenges: true,
        canCreateCustomChallenges: true,
        canAddFamilyMembers: false,
        maxFamilyMembers: 0,
      };
    default:
      // Default to most restrictive (individual)
      return getPlanLimits('individual');
  }
};

/**
 * Check if subscription has active access
 */
export const hasActiveSubscription = (subscription: Subscription | null): boolean => {
  if (!subscription) return false;
  return subscription.status === 'active' || subscription.status === 'trial';
};

/**
 * Get plan limits for a subscription
 */
export const getSubscriptionLimits = (subscription: Subscription | null): PlanLimits | null => {
  if (!subscription) return null;
  return getPlanLimits(subscription.planType);
};

/**
 * Check if action is allowed based on subscription
 */
export const canPerformAction = (
  subscription: Subscription | null,
  action: keyof PlanLimits,
): boolean => {
  if (!hasActiveSubscription(subscription)) {
    return false;
  }
  const limits = getSubscriptionLimits(subscription);
  if (!limits) return false;
  return limits[action] === true || (typeof limits[action] === 'number' && limits[action] > 0);
};

/**
 * Check if member count is within limits
 */
export const canAddMember = (
  subscription: Subscription | null,
  currentMemberCount: number,
): boolean => {
  if (!hasActiveSubscription(subscription)) {
    return false;
  }
  const limits = getSubscriptionLimits(subscription);
  if (!limits) return false;
  return currentMemberCount < limits.maxMembers;
};

/**
 * Check if can create custom challenge
 */
export const canCreateCustomChallenge = (
  subscription: Subscription | null,
  currentCustomChallengeCount: number,
): boolean => {
  if (!hasActiveSubscription(subscription)) {
    return false;
  }
  const limits = getSubscriptionLimits(subscription);
  if (!limits) return false;
  if (!limits.canCreateCustomChallenges) return false;
  // -1 means unlimited
  if (limits.maxCustomChallenges === -1) return true;
  return currentCustomChallengeCount < limits.maxCustomChallenges;
};

/**
 * Check if can add family member (individual plan only)
 */
export const canAddFamilyMember = (
  subscription: Subscription | null,
  currentFamilyMemberCount: number,
): boolean => {
  if (!hasActiveSubscription(subscription)) {
    return false;
  }
  const limits = getSubscriptionLimits(subscription);
  if (!limits) return false;
  if (!limits.canAddFamilyMembers) return false;
  return currentFamilyMemberCount < limits.maxFamilyMembers;
};

/**
 * Get feature availability message
 */
export const getFeatureUnavailableMessage = (planType: PlanType, feature: string): string => {
  const planNames: Record<PlanType, string> = {
    individual: 'Individual',
    starter: 'Starter',
    professional: 'Professional',
    enterprise: 'Enterprise',
  };
  
  const upgradeMessages: Record<PlanType, string> = {
    individual: 'Upgrade to Starter plan or higher',
    starter: 'Upgrade to Professional plan or higher',
    professional: 'Upgrade to Enterprise plan',
    enterprise: 'Feature available',
  };
  
  return `${feature} is not available on the ${planNames[planType]} plan. ${upgradeMessages[planType]}.`;
};

